import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStorageService } from '@/lib/service-context';
import { validateImageFile, failureMessage } from '@/lib/image-validation';
import { Button, Input, cn } from '@erp/ui';
import { ImageIcon, X, Loader2 } from '@/components/icons';

interface ImageUploadProps {
  value: string | null;
  onChange: (url: string | null) => void;
  bucket: string;
  pathPrefix: string;
  className?: string;
  disabled?: boolean;
}

export function ImageUpload({ value, onChange, bucket, pathPrefix, className, disabled }: ImageUploadProps) {
  const { t } = useTranslation();
  const storage = useStorageService();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    // Sprint 4 (audit M20): validate by magic bytes, not by file extension
    // or .type (which the browser sets from the extension and a malicious
    // user can spoof). SVG is rejected outright — the bucket serves files
    // back with their stored Content-Type, and SVG can carry script.
    const v = await validateImageFile(file);
    if (!v.ok) {
      setError(failureMessage(v.data.reason));
      return;
    }
    setUploading(true);
    try {
      // Use the canonical extension we derived from the magic bytes —
      // never trust file.name.
      const path = `${pathPrefix}${crypto.randomUUID()}.${v.data.ext}`;
      const url = await storage.upload(bucket, path, file);
      onChange(url);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Image upload failed:', msg);
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!value) return;
    try {
      const url = new URL(value);
      const pathParts = url.pathname.split(`/storage/v1/object/public/${bucket}/`);
      if (pathParts[1]) await storage.delete(bucket, pathParts[1]);
    } catch {
      // cleanup failed silently
    }
    onChange(null);
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
      {value ? (
        <div className="relative group rounded-lg overflow-hidden border bg-muted">
          <img src={value} alt="" className="w-full h-32 object-cover" />
          {!disabled && (
            <Button
              type="button"
              size="icon-xs"
              onClick={handleRemove}
              aria-label={t('common.delete')}
              className="absolute top-1.5 end-1.5 size-6 rounded-full bg-black/60 text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12} />
            </Button>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="w-full h-32 flex-col gap-2 border-2 border-dashed border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-primary"
        >
          {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImageIcon size={20} />}
          <span className="text-xs">{uploading ? t('common.loading') : t('articles.uploadImage')}</span>
        </Button>
      )}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
